import { motion, useInView } from 'framer-motion';
import { useMemo, useRef } from 'react';
import { Layers, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from '@/components/LocalizedLink';
import { TestimonialStack, type Testimonial } from '@/components/ui/glass-testimonial-swiper';
import { TESTIMONIALS, TESTIMONIALS_COPY } from '@/lib/testimonials';
import { getPortfolioProjectSlug } from '@/lib/portfolioProjects';

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, hsl(0 0% 38%), hsl(0 0% 10%))',
  'linear-gradient(135deg, hsl(0 0% 30%), hsl(0 0% 6%))',
  'linear-gradient(135deg, hsl(0 0% 44%), hsl(0 0% 16%))',
];

type TestimonialsSectionProps = {
  tag?: string;
  title?: string;
};

const TestimonialsSection = ({ tag, title }: TestimonialsSectionProps) => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const testimonials = useMemo<Testimonial[]>(
    () =>
      TESTIMONIALS.map((item, index) => ({
        id: item.id,
        initials: item.initials,
        name: item.brand,
        role: item.role[lang],
        quote: item.quote[lang],
        tags: item.tags.map((tagItem) => ({ text: tagItem.text[lang], type: tagItem.featured ? 'featured' : 'default' })),
        stats: [
          { icon: MapPin, text: item.market[lang] },
          { icon: Layers, text: item.deliverable[lang] },
        ],
        avatarGradient: AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length],
        href: `/projects/${getPortfolioProjectSlug(item.project)}`,
        hrefLabel: `${TESTIMONIALS_COPY.caseStudy[lang]} →`,
        renderLink: (href, children) => <Link to={href}>{children}</Link>,
      })),
    [lang],
  );

  return (
    <section ref={ref} className="section-y relative overflow-hidden" id="testimonials">
      <div className="absolute inset-0 bg-secondary/30" />
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head text-center"
        >
          <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
            {tag || t('testimonials.tag')}
          </p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">
            {title || t('testimonials.title')}
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            {TESTIMONIALS_COPY.intro[lang]}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto"
        >
          <TestimonialStack
            testimonials={testimonials}
            labels={{
              previous: TESTIMONIALS_COPY.previous[lang],
              next: TESTIMONIALS_COPY.next[lang],
              goTo: TESTIMONIALS_COPY.goTo[lang],
            }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
